import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { VirtualTable } from '@prio365/prio365-react-library';
import { Column } from '@prio365/prio365-react-library/lib/VirtualTable/types/Column';
import {
  ContactImportProcessDto,
  ExternalContactImportDto,
  EMPTY_GUID,
  UpdateContactCompanyFromImportProcessDto,
} from '../../../../../models/ContactImportProcess';
import { VirtualListItemOnRowProps } from '@prio365/prio365-react-library/lib/VirtualList/components/VirtualListItem';
import ContactImporterFilterBar from '../components/ContactImporterFilterBar';
import ExternalContactDrawer from '../components/ExternalContactDrawer';
import { apiUpdateContactsFromImportProcess } from '../../../api';

const normalizeAction = (action: any): string => {
  const NUM_TO_STR: Record<number, string> = {
    0: 'notset', 1: 'skip', 2: 'updatemissing', 3: 'overrideexisting', 4: 'create',
  };
  if (typeof action === 'number') return NUM_TO_STR[action] || String(action);
  return String(action).toLowerCase();
};

const getActionDisplay = (value: any): { color: string; label: string } => {
  const v = normalizeAction(value);
  const map: Record<string, { color: string; label: string }> = {
    'notset': { color: '#fa8c16', label: 'Nicht festgelegt' },
    'skip': { color: '#8c8c8c', label: 'Überspringen' },
    'updatemissing': { color: '#1890ff', label: 'Fehlende aktualisieren' },
    'overrideexisting': { color: '#1890ff', label: 'Bestehende überschreiben' },
    'create': { color: '#52c41a', label: 'Erstellen' },
  };
  return map[v] || { color: '#8c8c8c', label: String(value) };
};

interface ExternalContactTabProps {
  contactImportProcess: ContactImportProcessDto | null;
  onProcessUpdate: (updatedProcess: ContactImportProcessDto) => void;
}

export const ExternalContactTab: React.FC<ExternalContactTabProps> = ({
  contactImportProcess,
  onProcessUpdate,
}) => {
  const { t } = useTranslation();
  const [selectedContact, setSelectedContact] =
    useState<ExternalContactImportDto | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [actionFilter, setActionFilter] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [selectedItems, setSelectedItems] = useState<ExternalContactImportDto[]>([]);

  const filteredContacts = useMemo(() => {
    if (!contactImportProcess) return [];
    let items = contactImportProcess.externalContactImports ?? [];
    if (actionFilter !== null) {
      items = items.filter((c) => normalizeAction(c.action) === actionFilter);
    }
    if (searchText) {
      const lower = searchText.toLowerCase();
      items = items.filter(
        (c) =>
          c.firstName?.toLowerCase().includes(lower) ||
          c.lastName?.toLowerCase().includes(lower) ||
          c.emailAddresses?.some((e) =>
            e.address?.toLowerCase().includes(lower)
          )
      );
    }
    return items;
  }, [contactImportProcess, actionFilter, searchText]);

  const getPrimaryEmail = useCallback(
    (contact: ExternalContactImportDto): string => {
      const primary = contact.emailAddresses?.find((e) => e.isPrimary);
      return primary?.address || contact.emailAddresses?.[0]?.address || '';
    },
    []
  );

  const getPrimaryPhone = useCallback(
    (contact: ExternalContactImportDto): string => {
      const primary = contact.phoneNumbers?.find((p) => p.isPrimary);
      return primary?.number || contact.phoneNumbers?.[0]?.number || '';
    },
    []
  );

  const getCompanyName = useCallback(
    (companyId?: string): string => {
      if (!companyId || companyId === EMPTY_GUID) return '-';
      const company = (contactImportProcess?.externalCompanyImports ?? []).find(
        (c) => c.externalCompanyImportId === companyId
      );
      return company?.legalName || company?.displayName || companyId;
    },
    [contactImportProcess?.externalCompanyImports]
  );

  const handleRowClick: (
    item: ExternalContactImportDto
  ) => VirtualListItemOnRowProps<ExternalContactImportDto> = useCallback(
    (contact) => ({
      onClick: () => {
        setSelectedContact(contact);
        setIsDrawerOpen(true);
      },
    }),
    []
  );

  const closeDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedContact(null);
  };

  const handleBulkAction = async (action: string) => {
    if (!contactImportProcess || selectedItems.length === 0) return;

    const updateDto: UpdateContactCompanyFromImportProcessDto = {
      contactImportProcessId: contactImportProcess.contactImportProcessId,
      updateExternalContactImports: selectedItems.map((c) => ({
        externalContactImportId: c.externalContactImportId,
        externalCompanyImportId: c.externalCompanyImportId,
        externalBusinessUnitImportId: c.externalBusinessUnitImportId,
        salutation: c.salutation || '',
        firstName: c.firstName || '',
        lastName: c.lastName || '',
        title: c.title || '',
        department: c.department || '',
        position: c.position || '',
        website: c.website || '',
        notes: c.notes || '',
        emailAddresses: (c.emailAddresses ?? []).map((e) => ({
          externalEmailAddressImportId: e.externalEmailAddressImportId,
          address: e.address,
          notes: e.notes || '',
          isPrimary: e.isPrimary,
        })),
        phoneNumbers: (c.phoneNumbers ?? []).map((p) => ({
          externalPhoneNumberImportId: p.externalPhoneNumberImportId,
          number: p.number,
          notes: p.notes || '',
          phoneType: p.phoneType,
          isPrimary: p.isPrimary,
        })),
        addresses: (c.addresses ?? []).map((a) => ({
          externalAddressImportId: a.externalAddressImportId,
          name: a.name || '',
          street: a.street || '',
          zip: a.zip || '',
          city: a.city || '',
          country: a.country || '',
          region: a.region || '',
          secondAddressLine: a.secondAddressLine || '',
          notes: a.notes || '',
          isPrimary: a.isPrimary,
        })),
        bankAccounts: (c.bankAccounts ?? []).map((b) => ({
          externalBankAccountImportId: b.externalBankAccountImportId,
          creditInstitutionName: b.creditInstitutionName || '',
          bic: b.bic || '',
          iban: b.iban || '',
          notes: b.notes || '',
          isPrimary: b.isPrimary,
        })),
        action: action as any,
        existingContactId: c.existingContactId,
        existingCompanyId: c.existingCompanyId,
        existingBusinessUnitId: c.existingBusinessUnitId,
        description: c.description || '',
      })),
    };

    try {
      const result = await apiUpdateContactsFromImportProcess(
        contactImportProcess.contactImportProcessId,
        updateDto
      );
      if (result.result.ok) {
        const selectedIds = new Set(selectedItems.map((s) => s.externalContactImportId));
        const updatedProcess = {
          ...contactImportProcess,
          externalContactImports: (contactImportProcess.externalContactImports ?? []).map((c) =>
            selectedIds.has(c.externalContactImportId)
              ? { ...c, action: action as any }
              : c
          ),
        };
        onProcessUpdate(updatedProcess);
        setSelectedItems([]);
      }
    } catch (error) {
      console.error('Bulk update failed:', error);
    }
  };

  const columns: Column<ExternalContactImportDto>[] = [
    {
      id: 'firstName',
      alignSelf: true,
      width: 150,
      title: t('settings:companyImport.table.firstName'),
      accessor: 'firstName',
      Cell: (cellProps) => cellProps.value,
      sortingFn: (a, b) =>
        (a.firstName || '').localeCompare(b.firstName || ''),
    },
    {
      id: 'lastName',
      alignSelf: true,
      width: 150,
      title: t('settings:companyImport.table.lastName'),
      accessor: 'lastName',
      Cell: (cellProps) => cellProps.value,
      sortingFn: (a, b) =>
        (a.lastName || '').localeCompare(b.lastName || ''),
    },
    {
      id: 'email',
      alignSelf: true,
      width: 220,
      title: t('settings:companyImport.table.email'),
      accessor: 'externalContactImportId',
      Cell: ({ originalData }) => getPrimaryEmail(originalData),
      sortingFn: (a, b) =>
        getPrimaryEmail(a).localeCompare(getPrimaryEmail(b)),
    },
    {
      id: 'phone',
      alignSelf: true,
      width: 150,
      title: t('settings:companyImport.table.phone'),
      accessor: 'externalContactImportId',
      Cell: ({ originalData }) => getPrimaryPhone(originalData),
      sortingFn: (a, b) =>
        getPrimaryPhone(a).localeCompare(getPrimaryPhone(b)),
    },
    {
      id: 'company',
      alignSelf: true,
      width: 200,
      title: t('settings:companyImport.table.company'),
      accessor: 'externalCompanyImportId',
      Cell: (cellProps) => getCompanyName(cellProps.value),
      sortingFn: (a, b) =>
        getCompanyName(a.externalCompanyImportId).localeCompare(
          getCompanyName(b.externalCompanyImportId)
        ),
    },
    {
      id: 'description',
      alignSelf: true,
      width: 180,
      title: t('settings:companyImport.table.description'),
      accessor: 'description',
      Cell: (cellProps) => cellProps.value,
      sortingFn: (a, b) =>
        (a.description || '').localeCompare(b.description || ''),
    },
    {
      id: 'action',
      alignSelf: true,
      width: 150,
      title: t('settings:companyImport.table.action'),
      accessor: 'action',
      Cell: (cellProps) => {
        const { color, label } = getActionDisplay(cellProps.value);
        return (
          <span style={{
            padding: '2px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            backgroundColor: color,
            color: '#fff',
          }}>
            {label}
          </span>
        );
      },
      sortingFn: (a, b) => normalizeAction(a.action).localeCompare(normalizeAction(b.action)),
    },
  ];

  if (!contactImportProcess) {
    return <p>{t('settings:companyImport.noData')}</p>;
  }

  return (
    <>
      <ContactImporterFilterBar
        searchChangeHandler={(value) => setSearchText(typeof value === 'string' ? value : value?.target?.value ?? '')}
        actionFilter={actionFilter}
        onActionFilterChange={setActionFilter}
      />

      <VirtualTable<ExternalContactImportDto>
        columns={columns}
        data={filteredContacts}
        id="external-contact-table"
        resizable="absolute"
        rowsAreSelectable={true}
        onSelectionChange={setSelectedItems}
        selectedItems={selectedItems}
        onRow={handleRowClick}
        actionBarButtons={[
          {
            children: t('settings:companyImport.bulk.skip'),
            onClick: () => handleBulkAction('Skip'),
          },
          {
            children: t('settings:companyImport.bulk.create'),
            onClick: () => handleBulkAction('Create'),
          },
          {
            children: t('settings:companyImport.bulk.updateMissing'),
            onClick: () => handleBulkAction('UpdateMissing'),
          },
          {
            children: t('settings:companyImport.bulk.overrideExisting'),
            onClick: () => handleBulkAction('OverrideExisting'),
          },
        ] as any}
      />

      {selectedContact && (
        <ExternalContactDrawer
          visible={isDrawerOpen}
          onClose={closeDrawer}
          contact={selectedContact}
          contactImportProcess={contactImportProcess}
          onProcessUpdate={onProcessUpdate}
        />
      )}
    </>
  );
};
